import {
    Dialog,
    DialogPanel,
    DialogBackdrop,
    CloseButton,
} from "@headlessui/react";
import { Icon } from "@plura/client/components";

type ModalProps = {
    className?: string;
    classWrapper?: string;
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    isSlidePanel?: boolean;
    size?: "default" | "lg" | "xl" | "2xl";
    fitScreen?: boolean;
    noPadding?: boolean;
    /**
     * When true, ESC and backdrop clicks do NOT call `onClose` — only the
     * built-in X button (top-right) dismisses the modal. Use this to prevent
     * accidental dismissal of high-stakes flows (e.g. post-publish upsell)
     * where the user actively committed work and a stray click would lose
     * the offer/state.
     */
    disableClickOutside?: boolean;
};

const CloseButtonModal = ({ onClose }: { onClose?: () => void }) => {
    // When `onClose` is supplied we render a plain button that calls it
    // directly — this is the path used by `disableClickOutside`, where the
    // Dialog's own `onClose` is a no-op so ESC/backdrop don't dismiss but
    // the X button still has to. Otherwise we use Headless UI's
    // `CloseButton`, which fires the Dialog's `onClose` for free.
    if (onClose) {
        return (
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="group fixed right-5 top-5 z-15 size-12 border border-transparent bg-b-surface2-overlay rounded-full text-0 max-md:top-3 max-md:right-3 dark:border-s-stroke2 cursor-pointer"
            >
                <Icon
                    className="fill-t-secondary transition-colors group-hover:fill-t-primary"
                    name="close"
                />
            </button>
        );
    }
    return (
        <CloseButton className="group fixed right-5 top-5 z-15 size-12 border border-transparent bg-b-surface2-overlay rounded-full text-0 max-md:top-3 max-md:right-3 dark:border-s-stroke2">
            <Icon
                className="fill-t-secondary transition-colors group-hover:fill-t-primary"
                name="close"
            />
        </CloseButton>
    );
};

const sizeClasses = {
    default: "max-w-143",
    lg: "max-w-3xl",
    xl: "max-w-5xl",
    "2xl": "max-w-7xl",
};

const Modal = ({
    className,
    classWrapper,
    open,
    onClose,
    children,
    isSlidePanel,
    size = "default",
    fitScreen,
    noPadding,
    disableClickOutside,
}: ModalProps) => {
    // When `disableClickOutside` is set we hand the Dialog a no-op for its
    // dismiss handler so ESC and backdrop clicks become inert, then route
    // X-button clicks through `onClose` directly via `CloseButtonModal`'s
    // explicit handler path. The user can still close, just not by accident.
    const dialogOnClose = disableClickOutside ? () => { } : onClose;
    const closeButtonOnClose = disableClickOutside ? onClose : undefined;
    return (
        <Dialog className="relative z-50" open={open} onClose={dialogOnClose}>
            <DialogBackdrop
                className={`fixed inset-0 duration-300 ease-out data-[closed]:opacity-0 ${isSlidePanel
                    ? "bg-shade-07/70 dark:bg-shade-04/90"
                    : "bg-shade-04/90"
                    }`}
                transition
            />
            <div
                className={`fixed inset-0 flex w-screen ${isSlidePanel
                    ? "justify-end overflow-hidden"
                    : `justify-center p-4 ${fitScreen ? "overflow-hidden" : "overflow-y-auto"}`
                    } ${className || ""}`}
            >
                <DialogPanel
                    className={`bg-b-surface1 ${isSlidePanel
                        ? "relative w-114 h-svh duration-300 ease-out data-[closed]:translate-x-full max-md:w-full"
                        : `w-full ${sizeClasses[size]} m-auto ${noPadding ? "" : "p-12 max-md:p-6"} shadow-depth rounded-3xl duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 dark:shadow-[0px_2.15px_0.5px_-2px_rgba(0,0,0,0.25),0px_5px_1.5px_-4px_rgba(8,8,8,0.2),0px_6px_4px_-4px_rgba(8,8,8,0.16),0px_6px_13px_0px_rgba(8,8,8,0.12),0px_24px_24px_-16px_rgba(8,8,8,0.08)] ${fitScreen ? "h-[calc(100vh-2rem)] flex flex-col overflow-hidden" : ""}`
                        } ${classWrapper || ""}`}
                    transition
                >
                    {children}
                    {isSlidePanel && <CloseButtonModal onClose={closeButtonOnClose} />}
                </DialogPanel>
                {!isSlidePanel && <CloseButtonModal onClose={closeButtonOnClose} />}
            </div>
        </Dialog>
    );
};

export default Modal;
